import axios from 'axios';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Building2, Phone, Mail, MapPin, User, Trash2 } from 'lucide-react';
import MigrationBTInternesDialog from '@/components/MigrationBTInternesDialog';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function ListeClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreType, setFiltreType] = useState('tous');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // 🆕 [Phase 24] Dialog migration BT internes (owner only)
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/clients?actif=true`);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      
      // Trier par date_creation (plus récent en premier)
      const clientsTries = (data.clients || []).sort((a, b) => {
        return new Date(b.date_creation) - new Date(a.date_creation);
      });
      
      setClients(clientsTries);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await axios.delete(`${API_URL}/api/crm/clients/${deleteConfirm.id}`);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(`Client "${deleteConfirm.nom}" supprimé`);
      setDeleteConfirm(null);
      fetchClients();
    } catch (e) {
      // 🆕 2026-05-08 — Surface du détail serveur (utile pour diagnostiquer en prod)
      const detail = e.response?.data?.detail || e.message || 'Erreur inconnue';
      const code = e.response?.status ? ` (HTTP ${e.response.status})` : '';
      toast.error(`Erreur lors de la suppression${code} : ${detail}`, { duration: 8000 });
      // eslint-disable-next-line no-console
      console.error('[DELETE client]', e.response?.status, e.response?.data, e);
    }
  };

  const clientsFiltres = clients.filter((c) => {
    // 🆕 [7B] Null-safe : protège contre les clients legacy avec des champs
    //   undefined (ex: client interne avant backfill 7A, anciennes imports, etc.).
    //   Évite TypeError: Cannot read properties of undefined (reading 'toLowerCase').
    const s = (searchTerm || '').toLowerCase();
    const matchSearch =
      (c.nom || '').toLowerCase().includes(s) ||
      (c.telephone || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.ville_client || '').toLowerCase().includes(s);

    const matchType = filtreType === 'tous' || c.type_client === filtreType;

    return matchSearch && matchType;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1 text-sm">Gérez votre base de clients</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setShowMigrationDialog(true)}
            data-testid="btn-outils-admin"
            className="w-full sm:w-auto"
            title="Outils administrateur (migrations, etc.)"
          >
            🔧 Outils admin
          </Button>
          <Button
            onClick={() => navigate('/crm/creer-dossier')}
            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Client + Projet
          </Button>
        </div>
      </div>

      {/* 🆕 [Phase 24] Dialog migration BT internes */}
      <MigrationBTInternesDialog open={showMigrationDialog} onClose={() => setShowMigrationDialog(false)} />

      {/* Filtres */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, téléphone, email, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Filtre Type */}
            <Select value={filtreType} onValueChange={setFiltreType}>
              <SelectTrigger>
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les types</SelectItem>
                <SelectItem value="residentiel">Résidentiel</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
              <p className="text-sm text-gray-600">Total Clients</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {clients.filter((c) => c.type_client === 'residentiel').length}
              </p>
              <p className="text-sm text-gray-600">Résidentiels</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {clients.filter((c) => c.type_client === 'commercial').length}
              </p>
              <p className="text-sm text-gray-600">Commerciaux</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des clients */}
      {clientsFiltres.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              {searchTerm || filtreType !== 'tous'
                ? 'Aucun client ne correspond aux filtres'
                : 'Aucun client pour le moment'}
            </p>
            {clients.length === 0 && (
              <Button onClick={() => navigate('/crm/creer-dossier')}>
                <Plus className="w-4 h-4 mr-2" />
                Créer votre premier client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientsFiltres.map((client) => (
            <Card key={client.id} className="hover:shadow-lg transition-shadow h-full relative group">
              <Link to={`/crm/clients/${client.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-lg">{client.nom}</CardTitle>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {client.is_internal_client && (
                        <Badge
                          variant="outline"
                          className="border-purple-600 bg-purple-50 text-purple-700 text-[10px] px-1.5 py-0"
                          data-testid={`client-internal-badge-${client.id}`}
                        >
                          🏢 Interne
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={client.type_client === 'commercial' ? 'border-purple-600 text-purple-600' : ''}
                      >
                        {client.type_client === 'commercial' ? 'Commercial' : 'Résidentiel'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>{client.telephone}</span>
                    </div>
                    {client.email && (
                      <div className="flex items-center text-gray-600">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">
                        {client.ville_client || '—'}, {client.code_postal_client || '—'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirm(client); }}
                className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-red-50"
                title="Supprimer"
                data-testid={`delete-client-${client.id}`}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </Card>
          ))}
        </div>
      )}

      {/* Résultats */}
      <div className="mt-4 text-center text-sm text-gray-500">
        {clientsFiltres.length} client{clientsFiltres.length > 1 ? 's' : ''} affiché{clientsFiltres.length > 1 ? 's' : ''}
        {searchTerm && ` sur ${clients.length} total`}
      </div>

      {/* Dialog double confirmation suppression */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer un client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Êtes-vous sûr de vouloir supprimer le client <strong>"{deleteConfirm?.nom}"</strong> ?
            </p>
            <p className="text-sm text-red-600 font-medium">
              Cette action est irréversible. Tous les dossiers et soumissions associés resteront dans le système mais ne seront plus liés à ce client.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
              <Button variant="destructive" onClick={handleDeleteClient} data-testid="confirm-delete-client">
                <Trash2 className="w-4 h-4 mr-2" /> Oui, supprimer définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
